import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import { NavLink, useLocation } from 'react-router-dom'
import { AppBar, Button, IconButton, Menu, MenuItem, Toolbar } from '@mui/material'
import { styled } from '@mui/material/styles'

import {
  AISearchIcon,
  AnalyticsIcon,
  CaseIcon,
  ContactBookIcon,
  MenuIcon,
  PaymentIcon,
  ProcessIcon,
} from '~assets/icons'
import { Avatar } from '~components/common'
import { IS_LOCAL } from '~config'
import { lightTheme } from '~createTheme'
import TopHeader from '~pages/ConstituentScreens/components/TopHeader'
import QuickCreateDialog from '~pages/QuickCreateDialog'
import { RootState } from '~redux/rootReducer'
import { IdentityId, SkinEnum } from '~types'

import './layout.less'

type TopMenuProps = {}

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: theme.shadows[1],
  [theme.breakpoints.down('md')]: {
    width: '100vw',
  },
}))

const StyledToolbar = styled(Toolbar)({
  display: 'flex',
  justifyContent: 'space-between',
  padding: '0 16px',
})

const TopMenu: React.FC<TopMenuProps> = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [quickCreateOpen, setQuickCreateOpen] = useState<boolean>(false)
  const defaultOrganization = useSelector(
    (state: RootState) => state?.organization.selectedOrganization?.handle,
  )
  const user = useSelector((state: RootState) => state?.auth.user)
  const location = useLocation()

  const baseUrl = IS_LOCAL ? '' : 'https://app.polimorphic.com'

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const toggleQuickCreateDialog = useCallback(() => {
    setQuickCreateOpen((prev) => !prev)
  }, [])

  const menuItems = [
    {
      icon: ProcessIcon,
      label: 'Processes',
      nav: `${baseUrl}/processes`,
    },
    {
      icon: PaymentIcon,
      label: 'Payments',
      nav: `${baseUrl}/bills`,
    },
    {
      icon: AnalyticsIcon,
      label: 'Analytics',
      nav: `${baseUrl}/analytics`,
    },
    {
      icon: AISearchIcon,
      label: 'AI Settings',
      nav: `${baseUrl}/organizations/${defaultOrganization}/sources`,
    },
    {
      icon: MenuIcon,
      label: 'Menu',
      nav: `${baseUrl}/menu`,
    },
  ]

  if (user?.skin === SkinEnum.enum.Casual) {
    return <TopHeader />
  }

  return (
    <StyledAppBar position='fixed'>
      <StyledToolbar>
        <IconButton color='inherit' aria-label='menu' onClick={handleMenuOpen}>
          <MenuIcon color='var(--lightcarbon)' width={18} />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          {menuItems.map((item, index) => (
            <MenuItem key={index} onClick={handleMenuClose} component={NavLink} to={item.nav}>
              <div className='menu-item' style={{ display: 'flex', alignItems: 'center' }}>
                <div className='sidebar-icon'>
                  <item.icon
                    color='var(--lightcarbon)'
                    width={18}
                    gradient={location.pathname === item.nav}
                  />
                </div>
                <span style={{ marginLeft: '8px' }}>{item.label}</span>
              </div>
            </MenuItem>
          ))}
        </Menu>

        <IconButton color='inherit' component={NavLink} to={`${baseUrl}/cases`}>
          <CaseIcon
            color='var(--lightcarbon)'
            width={18}
            gradient={location.pathname === `${baseUrl}/cases`}
          />
        </IconButton>

        <Button
          color='inherit'
          onClick={toggleQuickCreateDialog}
          style={{
            width: '22px',
            height: '22px',
            minWidth: '22px',
            padding: 0,
            backgroundColor: lightTheme.palette.primary.main,
            color: 'white',
          }}
        >
          +
        </Button>

        <IconButton color='inherit' component={NavLink} to={`${baseUrl}/contacts`}>
          <ContactBookIcon
            color='var(--lightcarbon)'
            width={18}
            gradient={location.pathname === `${baseUrl}/contacts`}
          />
        </IconButton>

        {user && (
          <IconButton color='inherit' component={NavLink} to={`${baseUrl}/account`}>
            <Avatar identityId={user.identity_id as IdentityId} size={'30px'} />
          </IconButton>
        )}
      </StyledToolbar>
      <QuickCreateDialog open={quickCreateOpen} onClose={toggleQuickCreateDialog} />
    </StyledAppBar>
  )
}

export default React.memo(TopMenu)
